import { QuestionBulkChoicesType, QuestionChoiceBadge, QuestionChoiceModified, QuestionChoiceType, QuestionDisplayStyle, QuestionOptionKey, QuestionType, SubQuestionEntryModified, SubQuestionEntryType, SubQuestionModified, SubQuestionType, SubSurveyQuestionType, SurveyQuestionOptionType, SurveyQuestionType } from "@/common/interfaces";

export class EditorChoice {
	uuid: string;
	label: string;
	index: number;

	text: string | null;
	value: number | null;

	hasTextOption: boolean;
	isPinned: boolean;

	constructor (
		uuid: string, label: string, index: number, text: string | null, value: number | null, hasTextOption: boolean, isPinned: boolean
	) {
		this.uuid = uuid;
		this.label = label;
		this.index = index;
		this.text = text;
		this.value = value;
		this.hasTextOption = hasTextOption;
		this.isPinned = isPinned;
	}

	static fromRemote(remote: QuestionChoiceType): EditorChoice {
		return new EditorChoice(
			remote.uuid, remote.label, remote.index, remote.text, remote.value, remote.hasTextOption, remote.isPinned
		);
	}
}

export class EditorSubQuestion {
	uuid: string;
	label: string;
	index: number;
	subQuestionType: SubQuestionType;

	constructor(
		uuid: string, label: string, index: number, subQuestionType: SubQuestionType
	) {
		this.uuid = uuid;
		this.label = label;
		this.index = index;
		this.subQuestionType = subQuestionType;
	}

	static fromRemote(remote: SubSurveyQuestionType): EditorSubQuestion {
		return new EditorSubQuestion(
			remote.uuid, remote.label, remote.index, remote.subQuestionType
		);
	}
}


export class EditorSubQuestionEntry {
	uuid: string;
	label: string;
	index: number;

	constructor(
		uuid: string, label: string, index: number
	) {
		this.uuid = uuid;
		this.label = label;
		this.index = index;
	}

	static fromRemote(remote: SubQuestionEntryType): EditorSubQuestionEntry {
		return new EditorSubQuestionEntry(
			remote.uuid, remote.label, remote.index
		);
	}
}

/**
 * Represents an "instantiation" of a question in the surveyEditStore.
 *
 * Ultimately it acts as a "source of truth" for a question in the front end
 * also as a means of communication that can bridge question editor components
 * and the edit views without going through the `NssEditor`.
 *
 * Editor components are responsible for "creating" and registering these in the store.
 * As they are responsible for comes to the API, they should "own" the data.
 *
 * This is accomplished through methods on the stores. And the assumption
 * that this will be used as the source of truth
 * */
export class EditorQuestion {
	uuid: string;
	identifier: string;

	required: boolean;
	questionType: QuestionType;

	limitMax: number | null;
	limitMin: number | null;

	randomizeChoices: boolean;
	choiceBadge: QuestionChoiceBadge;
	displayStyle: QuestionDisplayStyle;

	options: Map<QuestionOptionKey, SurveyQuestionOptionType>;

	choices: Array<EditorChoice>;

	bulkChoices: QuestionBulkChoicesType | null;

	label: string | null;

	visible: boolean;

	hasPublished: boolean;

	subQuestions: Array<EditorSubQuestion>;//Array<SubSurveyQuestionType>;
	subEntries: Array<EditorSubQuestionEntry>;//Array<SubQuestionEntryType>;

	constructor(
		uuid: string,
		identifier: string,
		required: boolean,
		questionType: QuestionType,
		limitMax: number | null,
		limitMin: number | null,
		randomizeChoices: boolean,
		choiceBadge: QuestionChoiceBadge,
		displayStyle: QuestionDisplayStyle,
		options: Map<QuestionOptionKey, SurveyQuestionOptionType>,
		choices: Array<EditorChoice>,
		bulkChoices: QuestionBulkChoicesType | null,
		label: string | null,
		visible: boolean,
		hasPublished: boolean,
		subQuestions: Array<EditorSubQuestion>,
		subEntries: Array<EditorSubQuestionEntry>
	) {
		this.uuid = uuid;
		this.identifier = identifier;
		this.required = required;
		this.questionType = questionType;
		this.limitMax = limitMax;
		this.limitMin = limitMin;
		this.randomizeChoices = randomizeChoices;
		this.choiceBadge = choiceBadge;
		this.displayStyle = displayStyle;
		this.options = options;
		this.choices = choices;
		this.bulkChoices = bulkChoices;
		this.hasPublished = hasPublished;
		this.subQuestions = subQuestions;
		this.subEntries = subEntries;

		// This is only populated on load and served from the server
		//	this is updated on dispatchTransaction and load to keep
		//	relevant
		this.label = label;

		this.visible = visible;
	}

	getChoiceByUuid(choiceUuid: string): EditorChoice | null {
		const choice = this.choices.find(
			(ec: EditorChoice) => ec.uuid === choiceUuid
		);

		return (choice)? choice : null;
	}

	updateOptions(remoteQuestion: SurveyQuestionType): void {
		const options: Map<QuestionOptionKey, SurveyQuestionOptionType> = new Map();

		for (const [keyStr, val] of Object.entries(remoteQuestion.options)) {
			options.set(parseInt(keyStr) as QuestionOptionKey, val);
		}

		this.options = options;
	}

	updateSubQuestions(subQuestionModifications: Array<SubQuestionModified>): void {
		for (const modification of subQuestionModifications) {
			switch(modification.modification) {
				case 'add':
					if (modification.subQuestion) {
						this.subQuestions.splice(modification.subQuestion.index, 0, EditorSubQuestion.fromRemote(modification.subQuestion));
					}
				break;
				case 'edit':
					if (modification.subQuestion) {
						const index = this.subQuestions.findIndex(
							(sq: EditorSubQuestion) => sq.uuid === modification.subQuestionUuid
						);

						this.subQuestions[index] = EditorSubQuestion.fromRemote(modification.subQuestion);
					}
				break;
				case 'remove':
					const index = this.subQuestions.findIndex(
						(sq: SubSurveyQuestionType) => sq.uuid === modification.subQuestionUuid
					);

					if (index > -1) {
						this.subQuestions.splice(index, 1);
					}
				break;
				case 'reorder':
					if (modification.fromIndex !== null && modification.toIndex !== null) {
						const poppedSubQuestions = this.subQuestions.splice(modification.fromIndex, 1);

						if (poppedSubQuestions.length === 1) {
							this.subQuestions.splice(modification.toIndex, 0, ...poppedSubQuestions);
						}

						for (let i = 0; i < this.subQuestions.length; i++) {
							this.subQuestions[i].index = i;
						}
					}
				break;
			}
		}
	}

	updateSubEntries(subEntryModifications: Array<SubQuestionEntryModified>): void {
		for (const modification of subEntryModifications) {
			switch(modification.modification) {
				case 'add':
					if (modification.subQuestionEntry) {
						this.subEntries.splice(modification.subQuestionEntry.index, 0, EditorSubQuestionEntry.fromRemote(modification.subQuestionEntry));
					}
				break;
				case 'edit':
					if (modification.subQuestionEntry) {
						const index = this.subEntries.findIndex(
							(se: EditorSubQuestionEntry) => se.uuid === modification.subQuestionEntryUuid
						);

						this.subEntries[index] = EditorSubQuestionEntry.fromRemote(modification.subQuestionEntry);
					}
				break;
				case 'remove':
					const index = this.subEntries.findIndex(
						(se: EditorSubQuestionEntry) => se.uuid === modification.subQuestionEntryUuid
					);

					if (index > -1) {
						this.subEntries.splice(index, 1);
					}
				break;
				case 'reorder':
					if (modification.fromIndex !== null && modification.toIndex !== null) {
						const poppedEntries = this.subEntries.splice(modification.fromIndex, 1);

						if (poppedEntries.length === 1) {
							this.subEntries.splice(modification.toIndex, 0, ...poppedEntries);
						}

						for (let i = 0; i < this.subEntries.length; i++) {
							this.subEntries[i].index = i;
						}
					}
				break;
			}
		}
	}

	updateChoices(choiceModifications: Array<QuestionChoiceModified>): void {
		for (const modification of choiceModifications) {
			switch(modification.modification) {
				case 'add':
					if (modification.choice) {
						this.choices.splice(modification.choice.index, 0, EditorChoice.fromRemote(modification.choice));
					}
				break;
				case 'edit':
					if (modification.choice) {
						const index = this.choices.findIndex(
							(ec: EditorChoice) => ec.uuid === modification.choiceUuid
						);

						this.choices[index] = EditorChoice.fromRemote(modification.choice);
					}
				break;
				case 'remove':
					const index = this.choices.findIndex(
						(ec: EditorChoice) => ec.uuid === modification.choiceUuid
					);

					if (index > -1) {
						this.choices.splice(index, 1);
					}
				break;
				case 'reorder':
					if (modification.fromIndex !== null && modification.toIndex !== null) {
						const poppedChoices = this.choices.splice(modification.fromIndex, 1);

						if (poppedChoices.length === 1) {
							this.choices.splice(modification.toIndex, 0, ...poppedChoices);
						}

						for (let i = 0; i < this.choices.length; i++) {
							this.choices[i].index = i;
						}
					}
				break;
			}
		}
	}

	static fromRemote(remote: SurveyQuestionType): EditorQuestion {
		const options: Map<QuestionOptionKey, SurveyQuestionOptionType> = new Map();

		for (const [keyStr, val] of Object.entries(remote.options)) {
			options.set(parseInt(keyStr) as QuestionOptionKey, val);
		}

		return new EditorQuestion(
			remote.uuid,
			remote.identifier,
			remote.required,
			remote.questionType,
			remote.limitMax,
			remote.limitMin,
			remote.randomizeChoices,
			remote.choiceBadge,
			remote.displayStyle,
			options,
			remote.choices.map((rc: QuestionChoiceType) => EditorChoice.fromRemote(rc)),
			remote.bulkChoices,
			null,
			remote.visible,
			remote.hasPublished,
			remote.subQuestions.map((sq: SubSurveyQuestionType) => EditorSubQuestion.fromRemote(sq)),
			remote.subQuestionEntries.map((se: SubQuestionEntryType) => EditorSubQuestionEntry.fromRemote(se))
		);
	}
}
